import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  FB_STORY_PROMO_CTA_CLICK,
  FB_STORY_PROMO_DISMISS,
  FB_STORY_PROMO_SHOWN,
} from "../../../../constants/Events";
import { useAllAccounts } from "../../../../entities/accounts";
import {
  getAccountPlatformType,
  isAccountFacebookPage,
} from "../../../../entities/accounts/libs/account-platform";
import { useCanAddAccounts } from "../../../../entities/current-user";
import { useTrackEvent } from "../../../../libs/analytics";
import type { AccountType } from "../../../../shared/types/accounts";
import type { Post } from "../../../../shared/types/post";
import { isNewPostId } from "../../../../utils/posts";
import { postFormPickerSelectorAccounts } from "../account-selector-adapter";
import {
  dismissFbStoryPromo,
  isFbStoryPromoLive,
  isFbStoryPromoSuppressed,
  recordIfFacebookStory,
} from "./fb-story-promo";

export function useFbStoryPromo({
  post,
  isStory,
  selectedAccounts,
  enabled,
}: {
  post: Pick<Post, "_id" | "accountId" | "accountIds" | "isStory">;
  isStory: boolean;
  selectedAccounts: AccountType[];
  enabled: boolean;
}) {
  const allAccounts = useAllAccounts();
  const canAddAccounts = useCanAddAccounts();
  const trackEvent = useTrackEvent();
  // Read once per composer open; dismissing updates this state directly.
  const [suppressed, setSuppressed] = useState(isFbStoryPromoSuppressed);

  const facebookPages = useMemo(
    () => allAccounts.filter(isAccountFacebookPage),
    [allAccounts]
  );
  const pageLabels = useMemo(
    () =>
      postFormPickerSelectorAccounts(facebookPages, {
        instagramApiPublishing: false,
      }).map(({ label }) => label),
    [facebookPages]
  );

  const postId = post._id ?? "";
  const source = !post._id || isNewPostId(post._id) ? "new" : "edit";

  const visible =
    enabled &&
    isStory &&
    !suppressed &&
    isFbStoryPromoLive() &&
    selectedAccounts.length > 0 &&
    selectedAccounts.every(
      (account) => getAccountPlatformType(account) === "IG"
    );

  const metadata = useMemo(
    () => ({
      hasFacebookPage: facebookPages.length > 0,
      facebookPageCount: facebookPages.length,
      source,
      surface: "composer",
    }),
    [facebookPages.length, source]
  );

  // Opening a story that already goes to a Facebook page counts as scheduled.
  useEffect(() => {
    if (source !== "edit") return;
    const savedIds = new Set(
      post.accountIds?.length ? post.accountIds : [post.accountId]
    );
    const isFacebookStory = recordIfFacebookStory({
      isStory: post.isStory,
      accounts: facebookPages.filter((page) => savedIds.has(page._id)),
    });
    if (isFacebookStory) setSuppressed(true);
  }, [source, post.isStory, post.accountIds, post.accountId, facebookPages]);

  const shownFor = useRef<string | null>(null);
  useEffect(() => {
    if (!visible || shownFor.current === postId) return;
    shownFor.current = postId;
    trackEvent({ eventName: FB_STORY_PROMO_SHOWN, metadata });
  }, [visible, postId, metadata, trackEvent]);

  const onAddPage = useCallback(() => {
    trackEvent({ eventName: FB_STORY_PROMO_CTA_CLICK, metadata });
  }, [metadata, trackEvent]);

  const onDismiss = useCallback(() => {
    trackEvent({ eventName: FB_STORY_PROMO_DISMISS, metadata });
    dismissFbStoryPromo();
    setSuppressed(true);
  }, [metadata, trackEvent]);

  return { visible, pageLabels, canAddAccounts, onAddPage, onDismiss };
}
